import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PromoCard, PromoItem } from './PromoCard';
import { usePromoCarousel } from '../../../hooks/home/usePromoCarousel';
import { usePromoMetrics } from '../../../hooks/home/usePromoMetrics';

interface PromoCarouselProps {
  items: PromoItem[];
}

const SWIPE_VELOCITY = 0.35; // px/ms
const TRANSITION = 'transform 0.55s var(--apple-spring), opacity 0.55s var(--apple-spring)';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalize(n: number, len: number): number {
  return ((n % len) + len) % len;
}

export const PromoCarousel: React.FC<PromoCarouselProps> = ({ items }) => {
  // 即梦式流式自适应：ResizeObserver 实测【外层 w-full 包装层】宽度（HomeSection
  // 的 flex 居中壳），传入 usePromoMetrics 走容器实测路径——卡片/步距/透视全部
  // 按「实测宽/1440」等比换算，实测 ≤730 时自动切换窄屏单卡模式。
  // 必须观测包装层而非自身：自身宽度 = metrics 输出（width: containerWidth +
  // flex-grow: 0），观测自身会形成「只缩不涨」的反馈死锁——窗口收窄后内联宽度
  // 被旧实测值钉住，可用宽度回升（如侧栏折叠）时不会跟随增长。包装层 w-full
  // 宽度仅由列宽决定，与组件输出零耦合，收窄/放宽双向实时。
  // 旧写法传常量 1440 是整页缩放时代的锁定策略（注释称「响应式完全由外层整页
  // 缩放承担」），第 8 轮拆除整页 transform 框架后该前提失效：卡片尺寸不再随
  // 窗口变化，侧卡被裁切、单卡模式成为死分支——故改为实测驱动。
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const host = rootRef.current?.parentElement;
    if (!host) return;
    const update = () => setMeasuredWidth(host.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);
  const metrics = usePromoMetrics(measuredWidth);
  const { setIsPaused, currentIndex, goTo } = usePromoCarousel({ items });
  const [progress, setProgress] = useState(currentIndex);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartProgress = useRef(0);
  const dragStartTime = useRef(0);

  if (items.length === 0) return null;
  const len = items.length;

  // currentIndex 改变时，选择最短路径直接设置连续进度，由 CSS transition 完成动画
  useEffect(() => {
    if (isDragging) return;
    setProgress((prev) => {
      let target = currentIndex;
      const diff = target - prev;
      if (diff > len / 2) target -= len;
      if (diff < -len / 2) target += len;
      return target;
    });
  }, [currentIndex, isDragging, len]);

  // 动画结束后将 progress 归一化到 [0, len)，避免数值无限增长
  useEffect(() => {
    if (isDragging) return;
    const timer = setTimeout(() => {
      setProgress((prev) => normalize(prev, len));
    }, 600);
    return () => clearTimeout(timer);
  }, [currentIndex, isDragging, len]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragStartX.current = e.clientX;
    dragStartProgress.current = progress;
    dragStartTime.current = Date.now();
    setIsDragging(true);
    setIsPaused(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [progress, setIsPaused]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const dx = dragStartX.current - e.clientX;
    const dp = dx / metrics.step;
    setProgress(normalize(dragStartProgress.current + dp, len));
  }, [isDragging, len, metrics.step]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const dx = dragStartX.current - e.clientX;
    const dt = Date.now() - dragStartTime.current || 1;
    const velocity = dx / dt;

    setIsDragging(false);
    setIsPaused(false);

    let target = progress;
    if (Math.abs(velocity) > SWIPE_VELOCITY) {
      target = progress + (velocity > 0 ? 0.6 : -0.6);
    }
    goTo(Math.round(target));
  }, [isDragging, progress, goTo, setIsPaused]);

  // 键盘左右切换
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowLeft') goTo(currentIndex + 1);
      if (ev.key === 'ArrowRight') goTo(currentIndex - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, goTo]);

  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
  const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

  const singleMode = metrics.containerWidth <= 730;
  const centerCardWidth = singleMode ? metrics.containerWidth : metrics.cardWidth;
  const centerCardHeight = singleMode ? Math.round(centerCardWidth * 0.56) : metrics.cardHeight;
  const displayContainerHeight = singleMode ? centerCardHeight + 32 : metrics.containerHeight;
  // 指示点行间距/高度的缩放系数（设计基准 banner 卡高 280；1440 满宽时 = 1，版式不变）
  const dotK = displayContainerHeight / 280;

  const getVisualOffset = useCallback(
    (index: number) => {
      let offset = index - progress;
      if (offset > len / 2) offset -= len;
      if (offset < -len / 2) offset += len;
      return offset;
    },
    [progress, len]
  );

  const getItemStyle = useCallback(
    (index: number): React.CSSProperties => {
      const offset = getVisualOffset(index);
      const abs = Math.abs(offset);

      // 窄屏单卡片模式：只显示当前卡片，左右卡片隐藏
      if (singleMode) {
        const gap = 24;
        const translateX = offset * (centerCardWidth + gap);
        const opacity = isDragging ? clamp(1 - abs, 0, 1) : abs < 0.5 ? 1 : 0;
        const zIndex = Math.round(10 - abs * 10);
        return {
          transform: `translateX(calc(-50% + ${translateX}px))`,
          left: '50%',
          top: '50%',
          marginTop: -centerCardHeight / 2,
          zIndex,
          opacity,
          transition: isDragging ? 'none' : TRANSITION,
          pointerEvents: opacity > 0.2 ? ('auto' as const) : ('none' as const),
          width: `${centerCardWidth}px`,
          height: `${centerCardHeight}px`,
          willChange: 'transform, opacity',
        };
      }

      // 仅使用 transform 与 opacity 动画，避免 width/height 变化触发重排
      const translateX = offset * metrics.step;
      const translateZ = clamp(
        metrics.centerZ - abs * (metrics.centerZ - metrics.sideZ),
        metrics.sideZ,
        metrics.centerZ
      );
      const rotateY = clamp(-offset * metrics.rotateY, -metrics.rotateY, metrics.rotateY);
      // 中卡 scale=1（精确命中设计 500x280），向侧卡线性过渡到 sideScale；
      // 旧写法 clamp(1-abs*0.08, sideScale, 1) 因下限>上限导致所有卡恒放大 1.02
      const scale = 1 + Math.abs(offset) * (metrics.sideScale - 1);
      const opacity = abs > 1.5 ? 0 : 1;
      const zIndex = Math.round(10 - abs * 10);

      return {
        transform: `translateX(calc(-50% + ${translateX}px)) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
        left: '50%',
        top: '50%',
        marginTop: -metrics.cardHeight / 2,
        zIndex,
        opacity,
        transition: isDragging ? 'none' : TRANSITION,
        pointerEvents: opacity > 0.2 ? ('auto' as const) : ('none' as const),
        width: `${metrics.cardWidth}px`,
        height: `${metrics.cardHeight}px`,
        willChange: 'transform, opacity',
      };
    },
    [getVisualOffset, isDragging, metrics, singleMode, centerCardWidth, centerCardHeight]
  );

  const getSide = useCallback(
    (index: number) => {
      if (singleMode) return undefined;
      const offset = getVisualOffset(index);
      if (Math.abs(offset) > 0.5 && Math.abs(offset) < 1.5) {
        return offset < 0 ? 'right' : 'left';
      }
      return undefined;
    },
    [getVisualOffset, singleMode]
  );

  return (
    <div
      ref={rootRef}
      className="relative"
      style={{
        width: `${metrics.containerWidth}px`,
        flex: '0 1 auto',
        maxWidth: '100%',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* 轮播箭头：设计稿为 32x32 圆角方芯片（左 x 380..412 / 右 1748..1780，距容器边 20px），white/20 底 + 白 chevron，垂直居中于 banner */}
      <button
        onClick={goPrev}
        aria-label="上一个"
        className="absolute left-5 z-30 flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 transition-colors hover:bg-white/30"
        style={{ top: `${displayContainerHeight / 2}px`, transform: 'translateY(-50%)' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path
            d="M12.5 3 L6 9 L12.5 15"
            stroke="white"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <button
        onClick={goNext}
        aria-label="下一个"
        className="absolute right-5 z-30 flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 transition-colors hover:bg-white/30"
        style={{ top: `${displayContainerHeight / 2}px`, transform: 'translateY(-50%)' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path
            d="M5.5 3 L12 9 L5.5 15"
            stroke="white"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        className="relative flex items-center justify-center select-none mx-auto"
        style={{
          perspective: `${metrics.perspective}px`,
          width: `${metrics.containerWidth}px`,
          height: `${displayContainerHeight}px`,
          maxWidth: '100%',
          cursor: isDragging ? 'grabbing' : 'grab',
          overflow: 'hidden',
          touchAction: 'pan-y',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          className="absolute w-full"
          style={{ transformStyle: 'preserve-3d', height: `${displayContainerHeight}px` }}
        >
          {items.map((item, index) => {
            const isCenter = index === currentIndex;
            return (
              <div
                key={item.id}
                className="absolute flex items-center justify-center"
                style={getItemStyle(index)}
                onClick={() => {
                  if (!isDragging && index !== currentIndex) goTo(index);
                }}
              >
                <PromoCard
                  item={item}
                  active={isCenter}
                  side={getSide(index)}
                  width={singleMode ? centerCardWidth : metrics.cardWidth}
                  height={singleMode ? centerCardHeight : metrics.cardHeight}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* 指示点：设计 banner 总高 307（卡 280 + 间距 23 + 点行 4）；间距随卡高等比缩放，translate 保持视觉位置不变 */}
      <div
        className="flex items-center justify-center gap-2"
        style={{
          height: `${4 * dotK}px`,
          marginTop: `${23 * dotK}px`,
          transform: `translate(0px, ${-16 * dotK}px)`,
        }}
      >
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`h-1 rounded-sm transition-all ${
              currentIndex === index
                ? 'w-6 bg-white/[0.45]'
                : 'w-3 bg-white/[0.25] hover:bg-white/[0.35]'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
